import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, UserCheck, Package, PackageX, Bell, CalendarClock, Scale, Loader2, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { fmtGram, fmtDate, STATUS_COLORS } from "@/lib/format";
import { toast } from "sonner";

function Stat({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-surface border border-border rounded-md p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{label}</span>
        <Icon size={18} strokeWidth={1.5} className={accent ? "text-gold" : "text-muted-foreground"} />
      </div>
      <div className={`text-3xl font-semibold mt-3 ${accent ? "text-gold" : "text-foreground"}`}>{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [delivery, setDelivery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [s, o, st] = await Promise.all([api.get("/admin/dashboard"), api.get("/admin/orders"), api.get("/admin/settings")]);
        setStats(s.data);
        setRecent(o.data.slice(0, 6));
        setDelivery(st.data.default_delivery || "");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const saveSettings = async () => {
    try {
      const { data } = await api.put("/admin/settings", { default_delivery: delivery });
      setDelivery(data.default_delivery);
      toast.success("Varsayılan teslim süresi kaydedildi");
    } catch {
      toast.error("Kaydedilemedi");
    }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-gold" size={28} /></div>;

  return (
    <div>
      <h1 className="font-display text-3xl text-foreground mb-1">Dashboard</h1>
      <p className="text-sm text-muted-foreground mb-6">ORSE GOLD yönetim özeti</p>

      {stats.new_orders > 0 && (
        <div data-testid="new-order-alert" className="flex items-center gap-3 bg-gold/10 border border-gold/30 rounded-md px-4 py-3 mb-6">
          <Bell size={18} className="text-gold" />
          <span className="text-sm text-foreground">{stats.new_orders} yeni sipariş bekliyor.</span>
          <button onClick={() => navigate("/admin/orders")} className="ml-auto text-xs text-gold hover:underline">Görüntüle</button>
        </div>
      )}

      {stats.pending_gram > 0 && (
        <div data-testid="pending-gram-alert" className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/30 rounded-md px-4 py-3 mb-6">
          <Scale size={18} className="text-amber-400" />
          <span className="text-sm text-foreground">{stats.pending_gram} sipariş gerçek gram girişi bekliyor.</span>
          <button data-testid="pending-gram-cta" onClick={() => navigate("/admin/bulk-gram")} className="ml-auto text-xs text-amber-400 hover:underline">Toplu Gram Gir</button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <Stat testid="stat-total-customers" icon={Users} label="Toplam Müşteri" value={stats.total_customers} />
        <Stat testid="stat-active-customers" icon={UserCheck} label="Aktif Müşteri" value={stats.active_customers} />
        <Stat testid="stat-total-products" icon={Package} label="Toplam Ürün" value={stats.total_products} />
        <Stat testid="stat-out-of-stock" icon={PackageX} label="Stokta Olmayan" value={stats.out_of_stock} />
        <Stat testid="stat-new-orders" icon={Bell} label="Yeni Sipariş" value={stats.new_orders} accent />
        <Stat testid="stat-today-orders" icon={CalendarClock} label="Bugünkü Sipariş" value={stats.today_orders} />
        <Stat testid="stat-today-has" icon={Scale} label="Bugünkü Has (gr)" value={fmtGram(stats.today_has)} accent />
        <Stat testid="stat-month-has" icon={Scale} label="Bu Ay Has (gr)" value={fmtGram(stats.month_has)} accent />
      </div>

      <div className="bg-surface border border-border rounded-md p-5 mb-8" data-testid="settings-delivery">
        <div className="text-sm font-medium text-foreground mb-1">Varsayılan Teslim / Üretim Süresi</div>
        <p className="text-xs text-muted-foreground mb-3">Yeni siparişlere otomatik atanır. Sipariş bazında yine değiştirebilirsiniz.</p>
        <div className="flex gap-2">
          <input data-testid="default-delivery-input" value={delivery} onChange={(e) => setDelivery(e.target.value)} placeholder="örn. 7–10 iş günü" className="flex-1 h-10 rounded-md bg-obsidian border border-border text-foreground text-sm px-3 focus:outline-none focus:ring-1 focus:ring-gold" />
          <button data-testid="save-delivery-setting" onClick={saveSettings} className="px-4 h-10 bg-gold text-obsidian font-semibold rounded-md hover:bg-gold-hover transition-colors text-sm">Kaydet</button>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-medium text-foreground">Son Siparişler</h2>
        <button onClick={() => navigate("/admin/orders")} className="text-xs text-gold flex items-center gap-1">Tümü <ChevronRight size={14} /></button>
      </div>
      <div className="space-y-2">
        {recent.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Henüz sipariş yok.</p>}
        {recent.map((o) => (
          <div key={o.id} onClick={() => navigate("/admin/orders")} className="cursor-pointer flex items-center justify-between bg-surface border border-border rounded-md px-4 py-3 hover:border-gold transition-colors">
            <div>
              <span className="font-mono text-sm text-gold">{o.order_number}</span>
              <span className="text-sm text-muted-foreground ml-3">{o.company_name}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-foreground">Has {fmtGram(o.total_has)} gr</span>
              <span className={`text-[11px] px-2 py-1 rounded border ${STATUS_COLORS[o.status] || ""}`}>{o.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
